<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import { prizes, prizeSchedule } from '@/partners/lingo/lingo-giveaway/config/prizes'
import { T } from '@tolgee/vue'
import type { WonPrize } from '@/partners/lingo/lingo-giveaway/types/giveaway'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { DateTime } from 'luxon'

defineProps<{
  wonPrizes: WonPrize[]
}>()
defineEmits<{
  (e: 'return'): void
}>()

function getPrizeDate(raffleId: number) {
  const startDate = DateTime.fromISO('2024-10-22')
  const prizeDate = startDate.plus({ days: raffleId - 1 })
  return prizeDate.toFormat('DD')
}

</script>
<template>
  <div class="w-full flex flex-1 flex-col items-center gap-y-8 h-full overflow-hidden">
    <h2 class="text-2xl font-bold uppercase -tracking-[0.48px] text-light-white">
      <T
        key-name="lingo.giveaway.winners.your_wins"
        default-value="Your wins"
      />
    </h2>
    <div class="flex-1 overflow-hidden w-full h-full flex flex-col">
      <div class="flex flex-1 flex-col w-full h-full gap-y-3 overflow-y-auto scrollable-container">
        <div
          v-for="(prize, index) in Array.from(wonPrizes).reverse()"
          :key="index"
          class="py-2 pl-3 pr-1.5 bg-dark-top-layer rounded-xl flex justify-between items-center"
        >
          <p class="text-base font-semibold text-white">
            {{ getPrizeDate(prize.raffleId) }}
          </p>
          <div class="flex items-center gap-x-6 justify-end">
            <div
              v-if="prize.claimed"
              class="flex items-center gap-x-1.5"
            >
              <div class="size-4 rounded-full p-1 flex items-center justify-center bg-green-primary text-black">
                <CheckIcon />
              </div>
              <span class="text-base font-semibold text-green-primary">
                <T
                  key-name="lingo.giveaway.winners.claimed"
                  default-value="claimed"
                />
              </span>
            </div>
            <div class="size-12 min-w-12">
              <img
                class="h-full w-full"
                :src="prizes[prizeSchedule[+prize.raffleId as keyof typeof prizeSchedule]].src"
                :alt="prizes[prizeSchedule[+prize.raffleId as keyof typeof prizeSchedule]].name"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-col gap-y-3 w-full sticky pb-8 bottom-0 z-[10]"
    >
      <BaseButton
        class="w-full"
        style-type="tertiary"
        @click="$emit('return')"
      >
        <T
          key-name="lingo.giveaway.winners.return_cta"
          default-value="Return to overview"
        />
      </BaseButton>
      <p class="text-center px-2.5 font-medium text-base text-white/60 z-10">
        <T
          key-name="lingo.giveaway.winners.claim_info"
          default-value="You have 30 days to claim your prize from the moment the draw was made."
        />
      </p>
    </div>
  </div>
  <div
    v-if="wonPrizes.length"
    class="absolute w-full h-[50%] bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_11.49%,#000000_47.55%)] pointer-events-none"
  />
</template>
<style scoped>
.scrollable-container {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}
</style>